<template>
  <div class="order-statistics">
    <!-- 大数据运营中心标题 -->
    <div class="dashboard-title-wrapper">
      <div class="title-decoration left"></div>
      <h1 class="dashboard-title">大数据运营中心</h1>
      <div class="title-decoration right"></div>
    </div>

    <!-- 今日新增订单展示条 -->
    <div class="today-orders-bar">
      <div class="today-orders-title">今日新增订单</div>
      <div class="today-orders-list">
        <div class="today-order-item" v-for="(item, index) in todayOrders" :key="index">
          <div class="today-order-name">{{ item.name }}</div>
          <div class="today-order-count">+{{ item.count }}</div>
        </div>
      </div>
    </div>

    <!-- 主内容区域 - 左右布局 -->
    <div class="main-content-layout">
      <!-- 左侧：订单数据 -->
      <div class="left-panel">
        <div class="panel-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain size="small">
            返回首页
          </el-button>
          <div class="panel-title-with-count">
            <span class="panel-title">订单统计数据</span>
            <span class="total-count">(总数: {{ totalOrders }})</span>
          </div>
        </div>

        <!-- 分类订单数 - 3列2行 -->
        <el-row :gutter="15" class="order-stats-row">
          <el-col :span="8" v-for="(stat, index) in orderTypeStats" :key="index">
            <div :class="['mini-card', `mini-card-${index}`]">
              <div class="mini-number">{{ stat.count }}</div>
              <div class="mini-label">{{ stat.name }}</div>
            </div>
          </el-col>
        </el-row>

        <!-- 订单趋势图表 - TAB切换 -->
        <div class="trend-chart-container">
          <div class="trend-chart-header">
            <div class="chart-title">订单人数变化趋势</div>
          </div>
          <div class="trend-chart-body">
            <div class="tab-buttons">
              <div
                v-for="(item, index) in chartTabs"
                :key="index"
                :class="['tab-button', { active: activeTab === index }]"
                @click="switchTab(index)"
              >
                {{ item }}
              </div>
            </div>
            <div class="trend-chart-content">
              <div ref="trendChart" class="trend-chart" @click="handleChartClick"></div>
            </div>
          </div>
        </div>

        <!-- 本周订单分布饼图 -->
        <div class="week-distribution-container">
          <div class="distribution-header">
            <div class="chart-title">本周订单分布</div>
            <div class="week-total">总计: {{ weekTotal }} 单</div>
          </div>
          <div class="distribution-chart-wrapper">
            <div ref="distributionChart" class="distribution-chart"></div>
          </div>
        </div>
      </div>

      <!-- 右侧：用户数据可视化 -->
      <div class="right-panel">
        <div class="panel-title">用户数据分析</div>

        <!-- 图表1：用户增长曲线 -->
        <div class="chart-container">
          <div class="chart-title">用户增长趋势</div>
          <div ref="growthChart" class="chart"></div>
        </div>

        <!-- 图表2：年龄分布 -->
        <div class="chart-container">
          <div class="chart-title">用户年龄分布</div>
          <div ref="ageChart" class="chart"></div>
        </div>

        <!-- 图表3：性别比例变化 -->
        <div class="chart-container">
          <div class="chart-title">用户性别分布趋势</div>
          <div ref="genderChart" class="chart"></div>
        </div>

        <!-- 服务人员今日接单情况（暂时隐藏，接单量增加后可打开） -->
        <!-- <div class="staff-statistics-container">
          <div class="chart-title">服务人员今日接单情况</div>
          <div class="staff-grid-wrapper">
            <div class="staff-grid">
              <div class="staff-card" v-for="staff in staffStatistics" :key="staff.id">
                <div class="staff-name">{{ staff.name }}</div>
                <div class="staff-orders">今日: {{ staff.today_orders }}</div>
              </div>
            </div>
          </div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'OrderStatistics',
  setup() {
    const router = useRouter()
    const totalOrders = ref(0)
    const orderTypeStats = ref([])
    const growthChart = ref(null)
    const ageChart = ref(null)
    const genderChart = ref(null)
    const checkinChart = ref(null)
    const trendChart = ref(null)
    const distributionChart = ref(null)

    // 新增数据属性
    const todayOrders = ref([])
    const chartTabs = ref(['生活照料', '膳食服务', '清洁服务', '活动报名', '个性委托', '日常打卡'])
    const activeTab = ref(5)  // 默认选中"日常打卡"
    const staffStatistics = ref([])
    const trendData = ref({})
    const weekDistribution = ref([])
    const weekTotal = ref(0)

    let growthChartInstance = null
    let ageChartInstance = null
    let genderChartInstance = null
    let checkinChartInstance = null
    let trendChartInstance = null
    let distributionChartInstance = null

    // 获取订单统计数据
    const fetchOrderStatistics = async () => {
      try {
        const response = await axios.get('/order_statistics.php')
        if (response.data.code === 0) {
          totalOrders.value = response.data.total_orders
          orderTypeStats.value = response.data.type_stats
        } else {
          console.error('获取订单统计数据失败:', response.data.message)
        }
      } catch (error) {
        console.error('获取订单统计数据失败:', error)
      }
    }

    // 获取用户统计数据
    const fetchUserStatistics = async () => {
      try {
        const response = await axios.get('/user_statistics.php')
        if (response.data.code === 0) {
          await nextTick()
          initGrowthChart(response.data.data.growth_data)
          initAgeChart(response.data.data.age_distribution)
          initGenderChart(response.data.data.gender_data)
          initCheckinChart(response.data.data.checkin_data)
        } else {
          console.error('获取用户统计数据失败:', response.data.message)
        }
      } catch (error) {
        console.error('获取用户统计数据失败:', error)
      }
    }

    // 初始化用户增长曲线图
    const initGrowthChart = (data) => {
      if (!growthChart.value) return

      growthChartInstance = echarts.init(growthChart.value)

      const months = data.map(item => item.month)
      const counts = data.map(item => parseInt(item.count))

      const option = {
        backgroundColor: 'transparent',
        grid: {
          left: '10%',
          right: '10%',
          top: '15%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: months,
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } }
        },
        series: [{
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#00ffff',
            width: 3,
            shadowColor: 'rgba(0, 255, 255, 0.5)',
            shadowBlur: 10
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0, 255, 255, 0.3)' },
              { offset: 1, color: 'rgba(0, 212, 255, 0.05)' }
            ])
          },
          itemStyle: {
            color: '#00ffff',
            borderWidth: 2,
            shadowColor: 'rgba(0, 255, 255, 0.8)',
            shadowBlur: 8
          }
        }]
      }

      growthChartInstance.setOption(option)
    }

    // 初始化年龄分布图
    const initAgeChart = (data) => {
      if (!ageChart.value) return

      ageChartInstance = echarts.init(ageChart.value)

      const ageRanges = data.map(item => item.age_range)
      const counts = data.map(item => parseInt(item.count))

      const option = {
        backgroundColor: 'transparent',
        grid: {
          left: '10%',
          right: '10%',
          top: '15%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: ageRanges,
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } }
        },
        series: [{
          data: counts,
          type: 'bar',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#ff6b6b' },
              { offset: 0.2, color: '#ff8c42' },
              { offset: 0.4, color: '#ffd93d' },
              { offset: 0.6, color: '#6bcf7f' },
              { offset: 0.8, color: '#4d96ff' },
              { offset: 1, color: '#8e44ad' }
            ]),
            shadowColor: 'rgba(0, 212, 255, 0.5)',
            shadowBlur: 10
          },
          barWidth: '60%'
        }]
      }

      ageChartInstance.setOption(option)
    }

    // 初始化性别比例变化图
    const initGenderChart = (data) => {
      if (!genderChart.value) return

      genderChartInstance = echarts.init(genderChart.value)

      const months = data.map(item => item.month)
      const maleCounts = data.map(item => parseInt(item.male_count))
      const femaleCounts = data.map(item => parseInt(item.female_count))

      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10, 37, 64, 0.9)',
          borderColor: '#00d4ff',
          borderWidth: 1,
          textStyle: {
            color: '#00ffff'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>'
            params.forEach(item => {
              const ratio = (item.value / (maleCounts[item.dataIndex] + femaleCounts[item.dataIndex]) * 100).toFixed(1)
              result += `${item.marker} ${item.seriesName}: ${item.value}人 (${ratio}%)<br/>`
            })
            const total = maleCounts[params[0].dataIndex] + femaleCounts[params[0].dataIndex]
            result += `总计: ${total}人`
            return result
          }
        },
        legend: {
          data: ['男性用户', '女性用户'],
          textStyle: { color: '#66d9ff', fontSize: 12 },
          top: '5%'
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '20%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: months,
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 12 },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } },
          name: '累计人数',
          nameTextStyle: {
            color: '#66d9ff',
            fontSize: 12
          }
        },
        series: [
          {
            name: '男性用户',
            data: maleCounts,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#4d96ff',
              width: 3,
              shadowColor: 'rgba(77, 150, 255, 0.5)',
              shadowBlur: 10
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(77, 150, 255, 0.3)' },
                { offset: 1, color: 'rgba(77, 150, 255, 0.05)' }
              ])
            },
            itemStyle: {
              color: '#4d96ff',
              borderWidth: 2
            }
          },
          {
            name: '女性用户',
            data: femaleCounts,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#ff6b6b',
              width: 3,
              shadowColor: 'rgba(255, 107, 107, 0.5)',
              shadowBlur: 10
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255, 107, 107, 0.3)' },
                { offset: 1, color: 'rgba(255, 107, 107, 0.05)' }
              ])
            },
            itemStyle: {
              color: '#ff6b6b',
              borderWidth: 2
            }
          }
        ]
      }

      genderChartInstance.setOption(option)
    }

    // 初始化日常打卡图表
    const initCheckinChart = (data) => {
      if (!checkinChart.value) return

      checkinChartInstance = echarts.init(checkinChart.value)

      const dates = data.map(item => item.date.substring(5)) // 只显示 MM-DD
      const counts = data.map(item => parseInt(item.user_count))

      const option = {
        backgroundColor: 'transparent',
        grid: {
          left: '12%',
          right: '8%',
          top: '15%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: {
            color: '#66d9ff',
            fontSize: 10,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } }
        },
        series: [{
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#ffa500',
            width: 3,
            shadowColor: 'rgba(255, 165, 0, 0.5)',
            shadowBlur: 10
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(255, 165, 0, 0.3)' },
              { offset: 1, color: 'rgba(255, 165, 0, 0.05)' }
            ])
          },
          itemStyle: {
            color: '#ffa500',
            borderWidth: 2,
            shadowColor: 'rgba(255, 165, 0, 0.8)',
            shadowBlur: 8
          }
        }]
      }

      checkinChartInstance.setOption(option)
    }

    // 获取综合统计数据（今日订单、趋势、服务人员）
    const fetchDashboardStatistics = async () => {
      try {
        const response = await axios.get('/dashboard_statistics.php')
        console.log('Dashboard statistics response:', response.data)
        if (response.data.code === 0) {
          // 设置今日订单数据
          todayOrders.value = response.data.data.today_orders
          console.log('Today orders:', todayOrders.value)

          // 设置趋势数据
          trendData.value = response.data.data.trend_data
          console.log('Trend data:', trendData.value)

          // 设置服务人员统计（暂时隐藏，不需要获取）
          // staffStatistics.value = response.data.data.staff_statistics
          // console.log('Staff statistics:', staffStatistics.value)

          // 设置本周订单分布数据
          weekDistribution.value = response.data.data.week_distribution || []
          weekTotal.value = response.data.data.week_total || 0
          console.log('Week distribution:', weekDistribution.value)

          // 初始化图表
          await nextTick()
          initTrendChart()
          initDistributionChart()
        } else {
          console.error('获取统计数据失败:', response.data.message)
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    }

    // 初始化趋势图表
    const initTrendChart = () => {
      if (!trendChart.value) return

      if (!trendChartInstance) {
        trendChartInstance = echarts.init(trendChart.value)
      }

      // 每次初始化图表时都重新绑定事件（先解绑再绑定，避免重复）
      trendChartInstance.off('click')
      trendChartInstance.on('click', 'series', function(params) {
        console.log('图表被点击了，点击参数：', params)
        console.log('当前选中的tab索引：', activeTab.value)

        // 根据当前选中的 tab 跳转到对应的订单页面
        const orderRoutes = [
          '/orders/life-care',      // 生活照料
          '/orders/meal-service',   // 膳食服务
          '/orders/laundry-service',// 清洁服务
          '/orders/activity',       // 活动报名
          '/orders/custom-service', // 个性委托
          '/orders/daily-checkin'   // 日常打卡
        ]

        const targetRoute = orderRoutes[activeTab.value]
        console.log('准备跳转到：', targetRoute)

        if (targetRoute) {
          // 使用 Vue Router 进行路由跳转
          try {
            router.push(targetRoute).then(() => {
              console.log('路由跳转成功')
            }).catch((error) => {
              console.error('路由跳转失败：', error)
              // 如果路由跳转失败，使用直接URL跳转
              window.location.href = 'https://yanglao.ruihou.com' + targetRoute
            })
          } catch (e) {
            console.error('跳转出错：', e)
            // 备用方案：直接跳转
            window.location.href = 'https://yanglao.ruihou.com' + targetRoute
          }
        }
      })

      const tabNames = ['生活照料', '膳食服务', '清洁服务', '活动报名', '个性委托', '日常打卡']
      const currentTabName = tabNames[activeTab.value]
      const currentData = trendData.value[currentTabName] || []

      // 获取最近30天的日期
      const dates = []
      const dataMap = {}

      // 将数据转换为map以便查找
      currentData.forEach(item => {
        dataMap[item.date] = parseInt(item.count)
      })

      // 生成最近30天的日期和对应数据
      const fullDates = []
      for (let i = 29; i >= 0; i--) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]
        fullDates.push(dateStr)
        dates.push(dateStr.substring(5)) // 只显示MM-DD
      }

      // 获取对应的数据，如果没有数据则填充0
      const counts = fullDates.map((date) => {
        return dataMap[date] || 0
      })

      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10, 37, 64, 0.9)',
          borderColor: '#00d4ff',
          borderWidth: 1,
          textStyle: {
            color: '#00ffff'
          },
          formatter: function(params) {
            return params[0].name + '<br/>' +
                   '订单数: ' + params[0].value + '<br/>' +
                   '<span style="color: #ffa500;">点击查看详情</span>'
          }
        },
        grid: {
          left: '10%',
          right: '5%',
          top: '10%',
          bottom: '15%'
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: {
            color: '#66d9ff',
            fontSize: 10,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#00d4ff' } },
          axisLabel: { color: '#66d9ff', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(0, 212, 255, 0.1)' } }
        },
        series: [{
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#00ffff',
            width: 3,
            shadowColor: 'rgba(0, 255, 255, 0.5)',
            shadowBlur: 10
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0, 255, 255, 0.3)' },
              { offset: 1, color: 'rgba(0, 212, 255, 0.05)' }
            ])
          },
          itemStyle: {
            color: '#00ffff',
            borderWidth: 2,
            shadowColor: 'rgba(0, 255, 255, 0.8)',
            shadowBlur: 8
          }
        }]
      }

      trendChartInstance.setOption(option)
    }

    // TAB切换方法
    const switchTab = (index) => {
      activeTab.value = index
      initTrendChart()
    }

    // 处理图表点击事件（备用方案）
    const handleChartClick = () => {
      console.log('DIV容器被点击了，当前选中的tab索引：', activeTab.value)

      const orderRoutes = [
        '/orders/life-care',      // 生活照料
        '/orders/meal-service',   // 膳食服务
        '/orders/laundry-service',// 清洁服务
        '/orders/activity',       // 活动报名
        '/orders/custom-service', // 个性委托
        '/orders/daily-checkin'   // 日常打卡
      ]

      const targetRoute = orderRoutes[activeTab.value]
      console.log('准备跳转到：', targetRoute)

      if (targetRoute) {
        // 使用 Vue Router 跳转（与返回首页按钮保持一致）
        router.push(targetRoute)
      }
    }

    // 初始化本周订单分布饼图
    const initDistributionChart = () => {
      if (!distributionChart.value) return

      if (!distributionChartInstance) {
        distributionChartInstance = echarts.init(distributionChart.value)
      }

      // 如果没有数据，显示空状态
      if (!weekDistribution.value || weekDistribution.value.length === 0) {
        const option = {
          backgroundColor: 'transparent',
          title: {
            text: '暂无数据',
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#66d9ff',
              fontSize: 16
            }
          }
        }
        distributionChartInstance.setOption(option)
        return
      }

      // 定义科技风格的颜色
      const colors = [
        '#00ffff',  // 青色
        '#00d4ff',  // 天蓝
        '#4d96ff',  // 蓝色
        '#8e44ad',  // 紫色
        '#ff6b6b',  // 红色
        '#ffa500'   // 橙色
      ]

      const option = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)',
          backgroundColor: 'rgba(10, 37, 64, 0.9)',
          borderColor: '#00d4ff',
          borderWidth: 1,
          textStyle: {
            color: '#00ffff'
          }
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'center',
          textStyle: {
            color: '#66d9ff',
            fontSize: 12
          },
          icon: 'circle'
        },
        series: [{
          name: '订单分布',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['40%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#0a1929',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
              color: '#00ffff'
            },
            itemStyle: {
              shadowBlur: 20,
              shadowColor: 'rgba(0, 212, 255, 0.8)'
            }
          },
          labelLine: {
            show: false
          },
          data: weekDistribution.value.map((item, index) => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colors[index % colors.length] },
                { offset: 1, color: colors[index % colors.length] + '80' }
              ]),
              shadowBlur: 10,
              shadowColor: colors[index % colors.length]
            }
          }))
        }]
      }

      distributionChartInstance.setOption(option)
    }

    // 窗口大小改变时重新渲染图表
    const handleResize = () => {
      growthChartInstance?.resize()
      ageChartInstance?.resize()
      genderChartInstance?.resize()
      checkinChartInstance?.resize()
      trendChartInstance?.resize()
      distributionChartInstance?.resize()
    }

    onMounted(() => {
      fetchOrderStatistics()
      fetchUserStatistics()
      fetchDashboardStatistics()
      window.addEventListener('resize', handleResize)
    })

    return {
      totalOrders,
      orderTypeStats,
      growthChart,
      ageChart,
      genderChart,
      checkinChart,
      trendChart,
      distributionChart,
      todayOrders,
      chartTabs,
      activeTab,
      staffStatistics,
      weekDistribution,
      weekTotal,
      switchTab,
      handleChartClick
    }
  }
}
</script>

<style scoped>
.order-statistics {
  padding: 20px;
  min-height: calc(100vh - 120px);
  background: linear-gradient(135deg, #0a1929 0%, #0d2847 50%, #0a1929 100%);
  position: relative;
}

/* 大数据运营中心标题样式 */
.dashboard-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
}

.dashboard-title {
  font-size: 36px;
  font-weight: bold;
  color: #00ffff;
  text-align: center;
  letter-spacing: 12px;
  margin: 0 30px;
  padding: 15px 30px;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 3px solid #00d4ff;
  border-radius: 8px;
  box-shadow:
    0 0 30px rgba(0, 212, 255, 0.6),
    0 0 60px rgba(0, 168, 255, 0.4),
    inset 0 0 30px rgba(0, 212, 255, 0.2);
  text-shadow:
    0 0 15px rgba(0, 255, 255, 1),
    0 0 30px rgba(0, 212, 255, 0.8),
    0 0 45px rgba(0, 168, 255, 0.6),
    2px 2px 4px rgba(0, 0, 0, 0.8);
  animation: titlePulse 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;
}

.dashboard-title::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(0, 212, 255, 0.15) 50%, transparent 70%);
  animation: titleShine 4s linear infinite;
}

.title-decoration {
  width: 150px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #00ffff, #00d4ff);
  position: relative;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.8);
}

.title-decoration.left {
  background: linear-gradient(90deg, transparent, #00d4ff, #00ffff);
}

.title-decoration.right {
  background: linear-gradient(90deg, #00ffff, #00d4ff, transparent);
}

.title-decoration::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: #00ffff;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  box-shadow:
    0 0 10px rgba(0, 255, 255, 0.8),
    0 0 20px rgba(0, 212, 255, 0.6);
  animation: dotPulse 2s ease-in-out infinite;
}

.title-decoration.left::after {
  right: -6px;
}

.title-decoration.right::after {
  left: -6px;
}

@keyframes titlePulse {
  0%, 100% {
    box-shadow:
      0 0 30px rgba(0, 212, 255, 0.6),
      0 0 60px rgba(0, 168, 255, 0.4),
      inset 0 0 30px rgba(0, 212, 255, 0.2);
  }
  50% {
    box-shadow:
      0 0 40px rgba(0, 212, 255, 0.8),
      0 0 80px rgba(0, 168, 255, 0.5),
      inset 0 0 40px rgba(0, 212, 255, 0.3);
  }
}

@keyframes titleShine {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes dotPulse {
  0%, 100% {
    transform: translateY(-50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateY(-50%) scale(1.3);
    opacity: 0.8;
  }
}

.order-statistics::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    repeating-linear-gradient(0deg, rgba(0, 212, 255, 0.05) 0px, transparent 1px, transparent 40px, rgba(0, 212, 255, 0.05) 41px),
    repeating-linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0px, transparent 1px, transparent 40px, rgba(0, 212, 255, 0.05) 41px);
  pointer-events: none;
}

/* 主内容区域布局 */
.main-content-layout {
  display: flex;
  gap: 20px;
  margin-top: 20px;
  position: relative;
  z-index: 1;
}

/* 左侧面板 - 订单数据 */
.left-panel {
  flex: 0 0 48%;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 2px solid #00d4ff;
  border-radius: 10px;
  padding: 20px;
  box-shadow:
    0 0 20px rgba(0, 212, 255, 0.3),
    inset 0 0 20px rgba(0, 212, 255, 0.1);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.panel-title {
  color: #00ffff;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
  letter-spacing: 2px;
}

:deep(.panel-header .el-button) {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(0, 168, 255, 0.2) 100%);
  border: 1px solid #00d4ff;
  color: #00ffff;
  font-weight: bold;
  transition: all 0.3s ease;
}

:deep(.panel-header .el-button:hover) {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.4) 0%, rgba(0, 168, 255, 0.4) 100%);
  border-color: #00ffff;
  box-shadow: 0 0 15px rgba(0, 212, 255, 0.5);
}

.order-stats-row {
  margin-bottom: 15px;
}

/* 迷你卡片样式 */
.mini-card {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 2px solid #00d4ff;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow:
    0 0 10px rgba(0, 212, 255, 0.3),
    inset 0 0 10px rgba(0, 212, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.mini-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(0, 212, 255, 0.1) 50%, transparent 70%);
  animation: cardShine 3s linear infinite;
}

.mini-card:hover {
  transform: translateY(-3px);
  border-color: #00ffff;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.5),
    inset 0 0 15px rgba(0, 212, 255, 0.2);
}

.total-card {
  border-color: #00ffff;
  border-width: 3px;
}

.mini-number {
  font-size: 32px;
  font-weight: bold;
  color: #00ffff;
  margin-bottom: 5px;
  text-shadow:
    0 0 10px rgba(0, 255, 255, 0.8),
    0 0 20px rgba(0, 212, 255, 0.6);
  position: relative;
  z-index: 1;
  animation: numberGlow 2s ease-in-out infinite;
}

.mini-label {
  font-size: 14px;
  color: #66d9ff;
  font-weight: 500;
  letter-spacing: 1px;
  position: relative;
  z-index: 1;
}

/* 日常打卡图表容器 */
.checkin-chart-container {
  margin-top: 20px;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 2px solid #00d4ff;
  border-radius: 8px;
  padding: 15px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
}

.checkin-chart {
  height: 250px;
  width: 100%;
}

/* 右侧面板 - 用户数据 */
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.right-panel > .panel-title {
  color: #00ffff;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
  letter-spacing: 2px;
  text-align: center;
  margin-bottom: 10px;
}

/* 图表容器 */
.chart-container {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 2px solid #00d4ff;
  border-radius: 10px;
  padding: 15px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-title {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  text-shadow: 0 0 8px rgba(0, 255, 255, 0.8);
}

.chart {
  flex: 1;
  min-height: 200px;
}

@keyframes cardShine {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes numberGlow {
  0%, 100% {
    text-shadow:
      0 0 10px rgba(0, 255, 255, 0.8),
      0 0 20px rgba(0, 212, 255, 0.6);
  }
  50% {
    text-shadow:
      0 0 15px rgba(0, 255, 255, 1),
      0 0 30px rgba(0, 212, 255, 0.8);
  }
}

/* 今日新增订单条样式 */
.today-orders-bar {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 2px solid #00d4ff;
  border-radius: 8px;
  padding: 12px 20px;
  margin-bottom: 20px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
}

.today-orders-title {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  text-shadow: 0 0 8px rgba(0, 255, 255, 0.8);
}

.today-orders-list {
  display: flex;
  gap: 15px;
  overflow-x: auto;
}

.today-order-item {
  flex: 0 0 auto;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 1px solid #00d4ff;
  border-radius: 6px;
  padding: 8px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.today-order-item:hover {
  border-color: #00ffff;
  transform: translateY(-2px);
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.today-order-name {
  color: #66d9ff;
  font-size: 14px;
}

.today-order-count {
  color: #00ffff;
  font-size: 18px;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(0, 255, 255, 0.8);
}

/* 订单总数样式 */
.panel-title-with-count {
  display: flex;
  align-items: center;
  gap: 10px;
}

.total-count {
  color: #ffa500;
  font-size: 16px;
  font-weight: bold;
  text-shadow: 0 0 8px rgba(255, 165, 0, 0.8);
}

/* 趋势图表容器样式 */
.trend-chart-container {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 2px solid #00d4ff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
}

.trend-chart-header {
  margin-bottom: 15px;
}

.trend-chart-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* TAB按钮样式 */
.tab-buttons {
  display: flex;
  gap: 10px;
  overflow-x: auto;
}

.tab-button {
  flex: 0 0 auto;
  padding: 8px 15px;
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 1px solid #00d4ff;
  border-radius: 6px;
  color: #66d9ff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button:hover {
  border-color: #00ffff;
  color: #00ffff;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(0, 168, 255, 0.2) 100%);
}

.tab-button.active {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(0, 168, 255, 0.3) 100%);
  border-color: #00ffff;
  color: #00ffff;
  font-weight: bold;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.trend-chart-content {
  height: 300px;
}

.trend-chart {
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
}

/* 鼠标悬停时的提示效果 */
.trend-chart:hover::after {
  content: '点击查看订单详情';
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 10px;
  background: rgba(255, 165, 0, 0.9);
  color: white;
  font-size: 12px;
  border-radius: 4px;
  pointer-events: none;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 本周订单分布容器样式 */
.week-distribution-container {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 2px solid #00d4ff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.week-distribution-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(0, 212, 255, 0.05) 50%, transparent 70%);
  animation: cardShine 4s linear infinite;
  pointer-events: none;
}

.distribution-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  position: relative;
  z-index: 1;
}

.week-total {
  color: #ffa500;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 8px rgba(255, 165, 0, 0.8);
  background: rgba(10, 37, 64, 0.6);
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid rgba(255, 165, 0, 0.5);
}

.distribution-chart-wrapper {
  position: relative;
  z-index: 1;
}

.distribution-chart {
  height: 280px;
  width: 100%;
}

/* 服务人员统计容器样式 */
.staff-statistics-container {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.6) 0%, rgba(13, 58, 95, 0.6) 100%);
  border: 2px solid #00d4ff;
  border-radius: 10px;
  padding: 15px;
  box-shadow:
    0 0 15px rgba(0, 212, 255, 0.3),
    inset 0 0 15px rgba(0, 212, 255, 0.1);
}

.staff-grid-wrapper {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 5px;
}

/* 自定义滚动条 */
.staff-grid-wrapper::-webkit-scrollbar {
  width: 8px;
}

.staff-grid-wrapper::-webkit-scrollbar-track {
  background: rgba(0, 212, 255, 0.1);
  border-radius: 4px;
}

.staff-grid-wrapper::-webkit-scrollbar-thumb {
  background: #00d4ff;
  border-radius: 4px;
}

.staff-grid-wrapper::-webkit-scrollbar-thumb:hover {
  background: #00ffff;
}

.staff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.staff-card {
  background: linear-gradient(135deg, rgba(10, 37, 64, 0.8) 0%, rgba(13, 58, 95, 0.8) 100%);
  border: 1px solid #00d4ff;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  transition: all 0.3s ease;
}

.staff-card:hover {
  border-color: #00ffff;
  transform: translateY(-2px);
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.staff-name {
  color: #66d9ff;
  font-size: 14px;
  margin-bottom: 5px;
}

.staff-orders {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(0, 255, 255, 0.8);
}

/* 响应式设计 */
@media screen and (max-width: 1200px) {
  .main-content-layout {
    flex-direction: column;
  }

  .left-panel {
    flex: 0 0 auto;
  }

  .mini-number {
    font-size: 28px;
  }

  .dashboard-title {
    font-size: 28px;
    letter-spacing: 8px;
    padding: 10px 20px;
  }

  .staff-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}
</style> 